import { useEffect } from 'react';
import { errorModuleUrl } from './services/urlService';
import { CommonHelper } from './utils/commonHelper';

export function useGlobalErrorLogger(apiUrl = errorModuleUrl.logError) {
  useEffect(() => {
    const loggedErrors = new Set();
    const ERROR_DEBOUNCE_TIME = 1200;

    const createErrorHash = (message, stack) => {
      return `${message || ''}_${stack || ''}`.substring(0, 120);
    };

    const isWorthLogging = (message, stack) => {
      if (!message || message.trim() === '' || message === 'Unknown error') {
        return false;
      }

      // Very common useless/empty cases we want to skip
      const uselessPatterns = [
        'Script error',           // Often from cross-origin without CORS headers
        'Uncaught (in promise)',  // Generic when reason is not Error
        'Unhandled promise rejection',
      ];

      if (uselessPatterns.some(p => message.includes(p))) {
        return false;
      }

      // Require some meaningful stack or at least line number info
      if (!stack || stack.trim() === '' || stack === 'No stack available') {
        return false;
      }

      // Very short or useless stack traces
      if (stack.length < 15) {
        return false;
      }

      return true;
    };

    const logError = (errorData) => {
      if (!isWorthLogging(errorData.message, errorData.stack)) {
        return; // ← Skip sending useless/empty errors
      }

      const errorHash = createErrorHash(errorData.message, errorData.stack);

      if (loggedErrors.has(errorHash)) return;

      loggedErrors.add(errorHash);
      setTimeout(() => loggedErrors.delete(errorHash), ERROR_DEBOUNCE_TIME);

      fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorData),
        keepalive: true,
      }).catch((err) => {
        console.warn('Failed to send error log:', err);
      });
    };

    // ── Window Error Handler ───────────────────────────────
    const handleError = (event) => {
      const { message, filename, lineno, colno, error } = event;

      const user_details = CommonHelper.GetBrandInfoFromLocalStorage('user_details') || {};

      const errorPayload = {
        message: message || 'Unknown error',
        email: user_details.email || 'anonymous',
        name: user_details.first_name || 'Guest',
        ipAddress: '', // server will fill
        stack: '',
        location: window.location.href,
        userAgent: navigator.userAgent,
      };

      if (error?.stack) {
        errorPayload.stack = error.stack;
      } else if (filename && lineno) {
        const fileNameShort = filename.split('/').pop() || 'unknown.js';
        errorPayload.stack = `${message}\n    at ${fileNameShort}:${lineno}:${colno || 0}`;
      } else {
        errorPayload.stack = message + '\n    (no stack available)';
      }

      logError(errorPayload);
    };

    // ── Unhandled Promise Rejection ────────────────────────
    const handleUnhandledRejection = (event) => {
      const reason = event.reason;
      const user_details = CommonHelper.GetBrandInfoFromLocalStorage('user_details') || {};

      let errorMessage = 'Unhandled promise rejection';
      let stack = 'No stack available';

      if (reason instanceof Error) {
        errorMessage = reason.message || errorMessage;
        stack = reason.stack || stack;
      } else if (typeof reason === 'string') {
        errorMessage = reason;
        stack = ''; // string rejections usually have no stack
      } else if (reason && reason.message) {
        errorMessage = reason.message;
        stack = reason.stack || stack;
      }

      const errorPayload = {
        message: errorMessage,
        email: user_details.email || 'anonymous',
        name: user_details.first_name || 'Guest',
        ipAddress: '',
        stack,
        location: window.location.href,
        userAgent: navigator.userAgent,
      };

      logError(errorPayload);
    };

    window.addEventListener('error', handleError, { capture: true });
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError, { capture: true });
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [apiUrl]);
}